import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import type { Args as BaseArgs } from 'webapp/components/submit-button';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type SourceType from 'webapp/models-custom/source-type';

interface Args extends Omit<BaseArgs, 'submitInProgress' | 'onTrigger'> {
	sourceType: SourceType | null;
	onConnect: () => void;
	stopPropagation?: boolean;
}

interface SourceConnectButtonComponentInterface<T> {
	Args: T;
	Element: HTMLAnchorElement; // this is needed for ...attributes
}

export default class SourceConnectButtonComponent extends Component<
	SourceConnectButtonComponentInterface<Args>
> {
	@service('popup') declare popupService: Service.Popup;
	@service('connect') declare connectService: Service.Connect;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.eventManagerService.on(AppEvent.CONNECT_INITIALIZATION_FINISHED, this, () => {
			this.submitInProgress = false;
		});
	}

	get model() {
		return this.args.sourceType;
	}

	get buttonText() {
		let text = this.args.text;
		if (typeof text === 'undefined') {
			if (this.model?.isFileUpload) {
				text = 'Upload file';
			} else {
				text = 'Connect now';
			}
		}
		return text;
	}

	get buttonIcon() {
		if (typeof this.args.icon === 'undefined') {
			return this.model?.isFileUpload ? 'Upload' : 'Connect';
		} else {
			return this.args.icon;
		}
	}

	get extraClass() {
		const classes = ['connect-button'];
		if (this.args.extraClass) {
			classes.push(this.args.extraClass);
		}
		if (this.buttonIcon) {
			classes.push('button--with-icon');
		}
		return classes.join(' ');
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.CONNECT_INITIALIZATION_FINISHED, this);
	}

	@action
	connect() {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;
		this.args.onConnect();
	}
	<template>
		<div class="connect-button-wrapper">
			<SubmitButtonComponent
				...attributes
				@onTrigger={{this.connect}}
				@text={{this.buttonText}}
				@icon={{this.buttonIcon}}
				@theme={{@theme}}
				@size={{@size}}
				@ripplerEffect={{@ripplerEffect}}
				@isDisabled={{@isDisabled}}
				@noDelay={{@noDelay}}
				@tooltipText={{@tooltipText}}
				@extraClass={{this.extraClass}}
				@stopPropagation={{@stopPropagation}}
				@submitInProgress={{this.submitInProgress}}
			/>
		</div>
	</template>
}
